import {
	INVITED_APP_SHORTCUT_VARIANTS,
	INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS,
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedApsKey,
	type InvitedAppShortcutAllowedTargetAppKey,
	type InvitedAppShortcutRecommendedApp,
	type InvitedAppShortcutRecommendedAppInput,
	type InvitedAppShortcutVariant,
} from '../types';

/**
 * Check if the APS key is allowed
 * @param apsKey - The APS key to check if it is allowed
 * @returns True if the APS key is allowed, false otherwise
 */
export const isAllowedApsKey = (
	apsKey: string | undefined,
): apsKey is InvitedAppShortcutAllowedApsKey => {
	if (!apsKey) {
		return false;
	}
	return (INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS as unknown as string[]).includes(apsKey);
};

/**
 * Check if the target app key is one of the supported Invited App Shortcuts target apps.
 *
 * Primarily a helper for `parseInvitedAppShortcutEntityId`, which uses it to decide whether a
 * bare id or a parsed product segment names a supported target app. Consumers should generally
 * reach for the parser instead, since GRS `entityId`s are variant-encoded since PUMA-2245 and
 * will not match this predicate directly.
 *
 * @param targetApp - The target app key to check
 * @returns True if the target app key is supported, false otherwise
 */
export const isAllowedTargetAppForInvitedAppShortcuts = (
	targetApp: string | undefined,
): targetApp is InvitedAppShortcutAllowedTargetAppKey => {
	if (!targetApp) {
		return false;
	}
	return (INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS as unknown as string[]).includes(targetApp);
};

/**
 * Suffix that terminates a variant-encoded GRS `entityId`.
 *
 * MUST stay in sync with the GRS backend that produces these ids (PUMA-2245).
 * Exported so the encoding is asserted in one place rather than duplicated as a
 * literal across tests and call sites.
 */
export const INVITED_APP_SHORTCUT_ENTITY_ID_SUFFIX = 'shortcut';

/**
 * Matches a variant-encoded `entityId` of the form
 * `${product}-${variant}-shortcut`, e.g. `jira-animated-shortcut`.
 *
 * Both segments are captured loosely (not restricted to the known product /
 * variant unions) so the caller can decide how to degrade: an unknown *product*
 * is rejected outright, whereas an unknown *variant* degrades to `none`.
 */
const VARIANT_ENCODED_ENTITY_ID_PATTERN = new RegExp(
	`^([a-z0-9]+)-([a-z0-9]+)-${INVITED_APP_SHORTCUT_ENTITY_ID_SUFFIX}$`,
);

const isKnownVariant = (value: string): value is InvitedAppShortcutVariant =>
	(INVITED_APP_SHORTCUT_VARIANTS as unknown as string[]).includes(value);

/**
 * Parse a GRS recommendation `entityId` into its target app and lozenge variant.
 *
 * | `entityId`                      | Result                                     |
 * | ------------------------------- | ------------------------------------------ |
 * | `jira-animated-shortcut`        | `{ product: 'jira', variant: 'animated' }` |
 * | `confluence-static-shortcut`    | `{ product: 'confluence', variant: 'static' }` |
 * | `jira` (plain product)          | `{ product: 'jira', variant: 'none' }`     |
 * | `jira-bouncy-shortcut`          | `{ product: 'jira', variant: 'none' }`     |
 * | `not-a-real-app`, `''`, nullish | `null`                                     |
 *
 * Two distinct fallbacks, both deliberate:
 *
 * - **Unknown variant → `none`.** If GRS starts emitting a variant this client
 *   does not know about, the shortcut still renders (just without a lozenge)
 *   rather than vanishing.
 * - **Unknown product → `null`.** Preserves the pre-existing filtering
 *   behaviour of `isAllowedTargetAppForInvitedAppShortcuts`: an `entityId` that
 *   does not name a supported target app is dropped by the caller.
 *
 * Never throws.
 *
 * @param entityId - The raw `entityId` from a GRS recommendation
 * @returns The parsed app and variant, or `null` if the app is not supported
 */
export const parseInvitedAppShortcutEntityId = (
	entityId: string | undefined | null,
): InvitedAppShortcutRecommendedApp | null => {
	if (!entityId) {
		return null;
	}

	// A plain product id (what GRS emitted before variant encoding, and what it
	// emits again if the backend change is rolled back) carries no variant.
	if (isAllowedTargetAppForInvitedAppShortcuts(entityId)) {
		return { product: entityId, variant: 'none' };
	}

	const match = VARIANT_ENCODED_ENTITY_ID_PATTERN.exec(entityId);
	if (!match) {
		return null;
	}

	const [, product, variant] = match;
	if (!isAllowedTargetAppForInvitedAppShortcuts(product)) {
		return null;
	}

	return { product, variant: isKnownVariant(variant) ? variant : 'none' };
};

/**
 * Reduce `recommendedApps` to plain product keys.
 *
 * Use this when handing `recommendedApps` to an API that only deals in target
 * app keys (for example `targetProducts` on the hook context, or `eligibleApps`
 * on the collaborators provider), which deliberately stay variant-agnostic.
 *
 * @param apps - The recommended apps
 * @returns The target app keys, order preserved
 */
export const toRecommendedProducts = (
	apps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput>,
): ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey> => apps.map((app) => app.product);

/**
 * Get the app key by the APS key
 * @param apsKey - The APS key to get the app key for
 * @returns The app key
 */
export const getAppKeyByApsKey = (
	apsKey: InvitedAppShortcutAllowedApsKey,
): InvitedAppShortcutAllowedTargetAppKey => {
	switch (apsKey) {
		case 'JIRA_SOFTWARE':
			return 'jira';
		case 'CONFLUENCE':
			return 'confluence';
		default:
			// This should never happen, throw it just in case if we add a new APS key without adding a new app key
			throw new Error(`Invalid APS key: ${apsKey}`);
	}
};
